/**
 * WHAT A RECURRING BOUNTY SAYS ABOUT ITS PERIOD (Nick, 21 Oct) — the band, the bounty page
 * and the client's band all read it, so they cannot disagree.
 *
 * Before she finishes, the period is dated ("Today, Oct 21", "This week, Oct 19 – 25",
 * "October"): a bare "Today" never said it comes back. After, it says when it does
 * ("Done for today · back tomorrow").
 *
 * `periodStartsAt` / `periodEndsAt` are midnight at the start of this period and the next,
 * in the member's own zone, as the API computes them from `shared/recurrence/period.ts`.
 * Pass that zone as `timeZone`; the dates are named in the viewer's `locale`. The runtime
 * zone can be the previous day — a UTC midnight is still the evening before across the
 * Americas.
 *
 * Returns a catalog key under `reputation.bounty-period` plus its values, so each app
 * translates it with its own i18n. Null for a one-time bounty, or when the period is
 * missing or unreadable.
 */
export type BountyPeriodNote = {
  key: 'daily-open' | 'daily-done' | 'weekly-open' | 'weekly-done' | 'monthly-open' | 'monthly-done';
  values: Record<string, string>;
};

export type BountyPeriod = {
  cadence: string;
  periodStartsAt: string | null;
  periodEndsAt: string | null;
  periodTimeZone: string | null;
};

/** ES2021's `formatRange`, which the shared package's TS lib predates; every runtime we ship has it. */
type RangeFormatter = Intl.DateTimeFormat & { formatRange(start: Date, end: Date): string };

/** Half a day back from the next period's midnight: the period's last day, DST or not. */
const HALF_DAY_MS = 12 * 60 * 60 * 1000;

function readDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A date never splits across lines: on a phone "Oct 19 –" / "25" reads as two things.
 * No-break spaces hold the words together; a line may still break after a dash, so a word
 * joiner follows each one.
 */
function unbreakable(text: string): string {
  return text.replace(/\s/g, '\u00a0').replace(/[–—-]/g, '$&\u2060');
}

/** "octubre" → "Octubre": a month standing alone as a label starts with a capital. */
function capitalized(text: string, locale?: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

export function bountyPeriodNote(bounty: BountyPeriod, done: boolean, locale?: string): BountyPeriodNote | null {
  const { cadence } = bounty;
  if (cadence !== 'daily' && cadence !== 'weekly' && cadence !== 'monthly') return null;
  const starts = readDate(bounty.periodStartsAt);
  const ends = readDate(bounty.periodEndsAt);
  if (!starts || !ends) return null;

  const timeZone = bounty.periodTimeZone ?? undefined;
  const format = (at: Date, options: Intl.DateTimeFormatOptions) =>
    unbreakable(new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(at));
  const dayMonth: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  if (cadence === 'daily') {
    return done ? { key: 'daily-done', values: {} } : { key: 'daily-open', values: { date: format(starts, dayMonth) } };
  }
  if (cadence === 'weekly') {
    if (done) return { key: 'weekly-done', values: { weekday: format(ends, { weekday: 'long' }) } };
    const lastDay = new Date(ends.getTime() - HALF_DAY_MS);
    const formatter = new Intl.DateTimeFormat(locale, { ...dayMonth, timeZone }) as RangeFormatter;
    const range = unbreakable(formatter.formatRange(starts, lastDay));
    return { key: 'weekly-open', values: { range } };
  }
  const month = format(starts, { month: 'long' });
  return done
    ? { key: 'monthly-done', values: { month, date: format(ends, dayMonth) } }
    : { key: 'monthly-open', values: { month: capitalized(month, locale) } };
}
